import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { type KisApiMst } from '../types';
import { callAxiosGet, callAxios } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([
  AllCommunityModule,
  ClientSideRowModelModule,
]);

interface AccountRow extends KisApiMst {
  _dirty?: boolean;
  _isNew?: boolean;
}

const btnStyle = (bg: string): React.CSSProperties => ({
  padding: '1px 8px', background: bg, color: 'white', border: 'none',
  borderRadius: '3px', cursor: 'pointer', fontSize: '11px', marginRight: '4px',
});

const ActionCellRenderer = (props: any) => {
  const row = props.data as AccountRow;
  if (!row) return null;
  return (
    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', height: '100%' }}>
      {row._dirty && (
        <button style={btnStyle('#3b82f6')} onClick={(e) => { e.stopPropagation(); props.context.onSave(row); }}>저장</button>
      )}
      {/* 물리 삭제하지 않는다 — 잔고/주문 데이터 보존을 위해 정지 전환만 한다 */}
      {row._isNew ? (
        <button style={btnStyle('#6b7280')} onClick={(e) => { e.stopPropagation(); props.context.onDelete(row); }}>취소</button>
      ) : row.isActive !== false ? (
        <button style={btnStyle('#f59e0b')} onClick={(e) => { e.stopPropagation(); props.context.onDelete(row); }}>정지</button>
      ) : null}
    </span>
  );
};

const PortfolioManagement: React.FC = () => {
  const copyMenu = useGridCopyMenu({ excludeFields: ['appKey', 'appSecret'] });
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  const fetchAccounts = () => {
    setLoading(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/portfolio-management/list',
      params: {},
      onSuccess: (data) => {
        setAccounts(((data as AccountRow[]) || []).map(a => ({ ...a, _dirty: false, _isNew: false })));
        setError(null);
        setLoading(false);
      },
      onError: (errMsgs) => {
        setError(errMsgs?.join(', ') || '목록 조회 실패');
        setAccounts([]);
        setLoading(false);
      },
    });
  };

  const saveRow = useCallback((row: AccountRow) => {
    const url = row._isNew ? '/api/v1/portfolio-management/create' : '/api/v1/portfolio-management/update';
    callAxios({
      axiosInstance, m, logger, url,
      params: {
        id: row.id, userId: row.userId, initD: row.initD, initAmt: row.initAmt,
        appKey: row.appKey || null, appSecret: row.appSecret || null,
        accountNo: row.accountNo, htsId: row.htsId || null, isReal: row.isReal,
        tradePwd: (row as any).tradePwd || null,
        isActive: row.isActive !== false,
        broker: (row as any).broker || 'kis',
        acntType: row.acntType || 'stk',
      },
      onSuccess: (_data, res) => {
        const result = res?.data as any;
        if (result?.success) {
          m.alert(row._isNew ? '생성되었습니다.' : '수정되었습니다.', 'success');
          fetchAccounts();
        } else {
          setError(result?.error || '저장 실패');
        }
      },
      onError: (errMsgs, err) => {
        const serverError = (err as any)?.response?.data?.error;
        setError(serverError || errMsgs?.join(', ') || '저장 실패');
      },
      isGet: false,
    });
  }, [axiosInstance, m, logger]);

  const deleteRow = useCallback(async (row: AccountRow) => {
    if (row._isNew) {
      setAccounts(prev => prev.filter(a => a !== row));
      return;
    }
    if (!(await m.confirm?.('이 계정을 정지하시겠습니까?\n주문/체결이 중단됩니다. 잔고·주문 데이터는 보존되며 상태를 다시 「사용중」으로 되돌릴 수 있습니다.'))) return;
    const ajaxTimerId = m.showAjaxLoader();
    try {
      const response = await axiosInstance.delete(`/api/v1/portfolio-management/${row.id}`);
      m.hideAjaxLoader(ajaxTimerId);
      if ((response.data as any).success) {
        m.alert((response.data as any).message || '정지되었습니다.', 'success');
        fetchAccounts();
      } else {
        setError((response.data as any).error || '정지 실패');
      }
    } catch (err: any) {
      m.hideAjaxLoader(ajaxTimerId);
      setError(err?.response?.data?.error || err?.message || '정지 실패');
    }
  }, [axiosInstance, m]);

  const addNewRow = () => {
    setAccounts(prev => [{
      id: 0, userId: 0, initD: new Date().toISOString().slice(0, 10), initAmt: 0,
      appKey: '', appSecret: '', accountNo: '', htsId: '', isReal: false, isActive: true, updatedAt: '',
      broker: 'kis', acntType: 'stk',
      _dirty: true, _isNew: true,
    }, ...prev]);
  };

  useEffect(() => { fetchAccounts(); }, []);

  const columnDefs = useMemo<ColDef<AccountRow>[]>(() => [
    {
      field: 'id', headerName: 'ID', width: 55, cellStyle: { textAlign: 'right' },
      editable: (p) => !!p.data?._isNew,
      valueSetter: (p) => { p.data.id = parseInt(p.newValue) || 0; return true; },
    },
    { field: 'htsId', headerName: 'HTS ID', width: 100, editable: true },
    { field: 'accountNo', headerName: '계좌번호', width: 120, editable: true, cellStyle: { textAlign: 'center' } },
    {
      field: 'broker', headerName: '브로커', width: 80, editable: true, cellStyle: { textAlign: 'center' },
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['kis', 'kiwoom', 'sim'] },
      cellRenderer: (p: any) => {
        const v = p.value || 'kis';
        // sim = 가상체결거래소(내부). 실주문 경로에 섞이지 않는다.
        const color = v === 'sim' ? '#7c3aed' : v === 'kiwoom' ? '#0891b2' : '#1e40af';
        return <span style={{ color, fontWeight: 700 }}>{v.toUpperCase()}</span>;
      },
    },
    {
      // 주식/선물옵션 — 선물 계좌는 주식 주문·체결·잔고 경로에서 빠진다 (선물 발주는 미구현, 2026-11-03)
      field: 'acntType', headerName: '상품', width: 70, editable: true, cellStyle: { textAlign: 'center' },
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['stk', 'fut'] },
      cellRenderer: (p: any) => p.value === 'fut'
        ? <span style={{ color: '#b45309', fontWeight: 700 }}>선물옵션</span>
        : <span>주식</span>,
    },
    {
      field: 'isReal', headerName: '구분', width: 70, editable: true, cellStyle: { textAlign: 'center' },
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] },
      // SIM 계정은 실전/모의 구분이 의미 없다 — 항상 'SIM'으로 표시한다.
      cellRenderer: (p: any) =>
        p.data?.broker === 'sim'
          ? <span style={{ color: '#7c3aed', fontWeight: 700 }}>SIM</span>
          : (p.value ? '실전' : '모의'),
      valueSetter: (p) => { p.data.isReal = p.newValue === true || p.newValue === 'true'; return true; },
    },
    {
      field: 'isActive', headerName: '상태', width: 70, editable: true, cellStyle: { textAlign: 'center' },
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] },
      cellRenderer: (p: any) => {
        const on = p.value !== false;
        return (
          <span style={{ color: on ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
            {on ? '사용중' : '정지'}
          </span>
        );
      },
      valueSetter: (p) => { p.data.isActive = p.newValue === true || p.newValue === 'true'; return true; },
    },
    {
      field: 'userId', headerName: '소유자', width: 65, cellStyle: { textAlign: 'right' },
      editable: (p) => !!p.data?._isNew,
      valueSetter: (p) => { p.data.userId = parseInt(p.newValue) || 0; return true; },
    },
    { field: 'initD', headerName: '시작일', width: 110, editable: true, cellStyle: { textAlign: 'center' } },
    {
      field: 'initAmt', headerName: '초기금액', width: 130, editable: true, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? Number(p.value).toLocaleString() : '',
      valueSetter: (p) => { p.data.initAmt = parseFloat(p.newValue) || 0; return true; },
    },
    { field: 'appKey', headerName: 'App Key', flex: 1, minWidth: 150, editable: true, cellStyle: { fontSize: '11px' } },
    { field: 'appSecret', headerName: 'App Secret', flex: 1, minWidth: 150, editable: true, cellStyle: { fontSize: '11px' } },
    { field: 'tradePwd' as any, headerName: '거래비번', width: 80, editable: (p: any) => p.data?.isReal === true, cellStyle: { textAlign: 'center' },
      valueFormatter: (p: any) => p.data?.isReal ? (p.value ? '****' : '미설정') : '',
    },
    {
      field: 'updatedAt', headerName: '수정일시', width: 145, cellStyle: { textAlign: 'center', fontSize: '11px' },
      valueFormatter: (p) => {
        if (!p.value) return '';
        const d = new Date(p.value);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      },
    },
    {
      headerName: '', field: '_actions' as any, width: 120, pinned: 'right',
      sortable: false, filter: false, floatingFilter: false, resizable: false,
      cellRenderer: ActionCellRenderer,
    },
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    minWidth: 60, resizable: true, sortable: true, filter: false,
    cellStyle: { fontSize: '12px' },
  }), []);

  const context = useMemo(() => ({ onSave: saveRow, onDelete: deleteRow }), [saveRow, deleteRow]);

  const onCellValueChanged = useCallback((event: any) => {
    if (event.data) {
      event.data._dirty = true;
      gridRef.current?.api.refreshCells({ rowNodes: [event.node], columns: ['_actions'], force: true });
    }
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: '#1e40af', fontSize: '12px' }}>데이터 로드 중...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 조회 조건 영역 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>KIS API 계정 관리</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: '12px' }}>총 {accounts.length}건</span>
          <button onClick={fetchAccounts} style={{ padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>새로고침</button>
          <button onClick={addNewRow} style={{ padding: '5px 12px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>신규 등록</button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '4px', color: '#991b1b', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#991b1b', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
        </div>
      )}

      {/* 그리드 영역 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden' }}>
        <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
          <AgGridReact {...copyMenu.gridProps}
            ref={gridRef}
            rowData={accounts}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            context={context}
            animateRows={true}
            onCellValueChanged={onCellValueChanged}
            stopEditingWhenCellsLoseFocus={true}
            singleClickEdit={true}
            getRowStyle={(params) => {
              if (params.data?._isNew) return { backgroundColor: '#f0fdf4' };
              if (params.data?._dirty) return { backgroundColor: '#fffbeb' };
              if (params.data?.isActive === false) return { backgroundColor: '#f3f4f6', color: '#9ca3af' };
              return undefined;
            }}
          />
        </div>
      </div>
      {copyMenu.menu}
    </div>
  );
};

export default PortfolioManagement;
